import $i18n from 'panda-i18n';
import * as React from 'react';
import { forwardRef, useEffect, useState, } from 'react';
import classNames from 'classnames';
import { CnSearch } from '@/components/cn-search';
import SelectDrawer from '@/components/cn-select/select-drawer/select-drawer';
import isFunction from 'lodash/isFunction';
import { isUndef, isValidArray } from '@/utils/func';
import { find, getDataByValues, getFirstValue, getFullPathByValue, getTreeDepth, } from '@/utils/tree';
import { CnTab, CnTabItem } from '../cn-tab';
import { useRequest } from 'ahooks';
import { handleRequestService } from './service';
import { CascaderItemList } from './cascader-item-list';
import { cascaderFilter } from './utils';
import { CnList, CnListItem } from '@/components/cn-list';
import { CnReadonly } from '@/components/cn-readonly';
const getInitValue = (props) => {
    const { dataSource = [], defaultValue, value } = props;
    let ret = [];
    if ('value' in props) {
        if (isValidArray(value)) {
            ret = value;
        }
        else if (!Array.isArray(value) && value) {
            ret = getFullPathByValue(dataSource, value);
        }
    }
    else if (!isUndef(defaultValue)) {
        if (isValidArray(defaultValue)) {
            ret = defaultValue;
        }
        else if (!Array.isArray(defaultValue) && defaultValue) {
            ret = getFullPathByValue(dataSource, defaultValue);
        }
    }
    return ret;
};
const getInitInternalValue = (props) => {
    const r = getInitValue(props);
    const isNormalMode = props.mode !== 'mini';
    if (r.length === 0) {
        if (!isNormalMode) {
            return getFirstValue(props.dataSource);
        }
        else {
            return [];
        }
    }
    else {
        return r;
    }
};
// eslint-disable-next-line
const defaultRenderContent = (values = [], separator) => Array.isArray(values) ? values.map((val) => val.label).join(separator) : '';
const CascaderSelect = (props, ref) => {
    const { prefix = 'cn-ui-m-', mode = 'cascade', dataSource: propDataSource = [], requestConfig = {}, enableRemoteLazyLoad, separator = '/', value, defaultValue, className, hasClear, useSimpleValue = true, onChange, rootTipText = $i18n.get({
        id: 'PleaseSelect',
        dm: '请选择',
        ns: 'CnCascaderSelect',
    }), loadData: propLoadData, isPreview, readOnly, renderPreview, size = 'medium', drawerClassName, previewClassName: previewClassNameProp, previewStyle, showSearch, filterLocal, onSelect = () => { }, onClear = () => { }, onSearch, onCancel, onVisibleChange, renderContent = defaultRenderContent, ...others } = props;
    const clsPrefix = `${prefix}cascader`;
    const isControlled = 'value' in props;
    const isNormalMode = true;
    const previewClassName = classNames(previewClassNameProp, `${prefix}select-preview--${size}`);
    // for field show
    const [curVal, setVal] = useState(getInitValue(props));
    // cur selected value, for internal show
    const [internalValue, setInternalVal] = useState(getInitInternalValue(props));
    const [loadingNode, setLoadingNode] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [tabActiveIndex, setTabActiveIndex] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    // 请求服务封装
    const requestService = handleRequestService(requestConfig);
    const isRemoteDataSource = React.useMemo(() => {
        return !!(requestConfig?.url || requestConfig?.service);
    }, [requestConfig]);
    const { runAsync, run, data = [], } = useRequest(requestService, {
        ready: isRemoteDataSource,
        ...requestConfig,
    });
    const dataSource = isRemoteDataSource ? data : propDataSource;
    const loadData = isRemoteDataSource && enableRemoteLazyLoad
        ? (item) => {
            return runAsync({
                [requestConfig.remoteLazyLoadKey || 'value']: item.value,
            }).catch((err) => {
                console.log(err);
            });
        }
        : propLoadData;
    const innerSelectedPath = getDataByValues(dataSource, internalValue);
    // 通过索引缓存每个选项的位置
    const itemCache = React.useMemo(() => {
        const res = {};
        if (!showSearch)
            return res;
        const loop = (data, prefix = '0') => data.forEach((item, index) => {
            const { children } = item;
            const pos = `${prefix}-${index}`;
            res[pos] = { ...item, pos };
            if (children && children.length) {
                loop(children, pos);
            }
        });
        loop(dataSource);
        return res;
    }, [dataSource, showSearch, filterLocal]);
    // 处理确定按钮点击
    const handleOk = () => {
        if (isNormalMode) {
            if (isValidArray(internalValue)) {
                const lastVal = internalValue[internalValue.length - 1];
                const n = find(dataSource, (node) => node.value === lastVal);
                if (n && n.children && n.children.length) {
                    handleCancel('invalid-selection');
                    return;
                }
            }
            else {
                handleCancel('invalid-selection');
                return;
            }
        }
        if (!isControlled) {
            setVal(internalValue);
        }
        setSearchValue('');
        if (onChange) {
            const selectedPath = getDataByValues(dataSource, internalValue);
            const lastData = selectedPath[selectedPath.length - 1];
            onChange(useSimpleValue ? lastData.value || '' : internalValue, lastData, {
                selectedPath,
            });
        }
    };
    // 处理取消
    const handleCancel = (reason) => {
        setInternalVal(curVal);
        setSearchValue('');
        if (isFunction(onCancel)) {
            onCancel(reason);
        }
    };
    // 处理复杂模式，列表点击
    const handleListClick = async (item, dpt) => {
        const val = item.value;
        let newInternalVal = [...internalValue.slice(0, dpt), val];
        if (loadData && internalValue.indexOf(val) > -1) {
            newInternalVal = internalValue.slice(0, internalValue.indexOf(val) + 1);
        }
        setInternalVal(newInternalVal);
        if (loadData && !item.isLeaf) {
            setLoadingNode(val);
            await loadData(item);
            setLoadingNode(null);
        }
        if (onSelect) {
            onSelect(val, item);
        }
    };
    const handleClear = () => {
        if (!isControlled) {
            setVal([]);
            setInternalVal([]);
        }
        if (onClear) {
            onClear();
        }
        setSearchValue('');
        if (onChange) {
            onChange(useSimpleValue ? '' : [], {}, {
                selectedPath: [],
            });
        }
    };
    const handleSearch = (v) => {
        setSearchValue(v);
        // 本地搜索不做处理
        if (filterLocal)
            return;
        // 如果配置了onSearch，则执行onSearch
        if (isFunction(onSearch)) {
            onSearch(v);
            return;
        }
        // 如果配置了远程数据源，则调用一次run方法，传入搜索的关键词作为参数
        if (isRemoteDataSource) {
            run({
                [requestConfig.searchKey || 'key']: v,
            });
        }
    };
    const handleVisibleChange = (visible, reason) => {
        setDrawerVisible(visible);
        if (isFunction(onVisibleChange)) {
            onVisibleChange(visible, reason);
        }
    };
    useEffect(() => {
        setVal(getInitValue({ ...props, dataSource }));
        setInternalVal(getInitInternalValue({ ...props, dataSource }));
    }, [value]);
    useEffect(() => {
        if (!drawerVisible && dataSource?.length) {
            setVal(getInitValue({ ...props, dataSource }));
            setInternalVal(getInitInternalValue({ ...props, dataSource }));
        }
    }, [dataSource]);
    const levels = React.useMemo(() => {
        const ret = [];
        let currentOptions = dataSource;
        let reachedEnd = false;
        if (isValidArray(internalValue)) {
            for (const v of internalValue) {
                const target = currentOptions.find((option) => option.value === v);
                ret.push({
                    selected: target,
                    options: currentOptions,
                });
                if (!target || !target.children || !target.children.length) {
                    reachedEnd = true;
                    break;
                }
                currentOptions = target.children;
            }
        }
        if (!reachedEnd) {
            ret.push({
                selected: undefined,
                options: currentOptions,
            });
        }
        return ret;
    }, [internalValue, dataSource]);
    useEffect(() => {
        setTabActiveIndex(levels.length - 1);
    }, [internalValue, levels.length]);
    if (isPreview || readOnly) {
        if (isFunction(renderPreview)) {
            return (React.createElement("div", { className: previewClassName, style: previewStyle }, renderPreview(innerSelectedPath)));
        }
        return (React.createElement(CnReadonly, null, innerSelectedPath.map((node) => node.label).join(separator)));
    }
    const depth = getTreeDepth(dataSource);
    const search = showSearch ? (React.createElement("div", { className: `${prefix}cascader-search-wrapper`, style: { width: '100%', height: '100rpx' } },
        React.createElement(CnSearch, { value: searchValue, hasClear: true, onChange: handleSearch, onClear: () => {
                handleSearch('');
            } }))) : null;
    const filteredDatasource = React.useMemo(() => {
        if (showSearch) {
            return cascaderFilter(itemCache, searchValue, !!filterLocal);
        }
        return [];
    }, [itemCache, showSearch, filterLocal, searchValue]);
    let content;
    if (showSearch && searchValue) {
        content = (React.createElement("div", { className: `${clsPrefix}-search-body` }, filteredDatasource.length === 0 ? (React.createElement("div", { className: `${clsPrefix}-not-found` }, $i18n.get({
            id: 'NoDataForTheTimeBeing_958511081',
            dm: '暂无数据',
            ns: 'CnCascaderSelect',
        }))) : (React.createElement(CnList, null, filteredDatasource.map((i) => {
            const optionChecked = internalValue.length === i.length &&
                internalValue.every((v, index) => v === i[index].value);
            const optionValue = i.map((v) => v.value);
            return (React.createElement(CnListItem, { className: classNames({
                    [`${clsPrefix}-search-option--selected`]: optionChecked,
                }), key: optionValue.join('/'), title: i.map((v) => v.label).join('/'), onClick: () => {
                    setInternalVal(optionValue);
                }, disabled: i.some((v) => v.disabled) }));
        })))));
    }
    else {
        content =
            mode === 'tree' ? (React.createElement("div", { className: `${clsPrefix}-tree` }, levels.map((level, index) => {
                const val = level.selected ? level.selected.value : null;
                const columnWidth = `${level.options.length === 0 ? 100 : 100 / depth}%`;
                const columnStyle = { width: columnWidth };
                // 只有2列时，第一列为33.3，第二列为66.7
                if (depth === 2 && index === 0) {
                    columnStyle.width = '33.3%';
                }
                if (depth === 2 && index === 1) {
                    columnStyle.width = '66.7%';
                    columnStyle.backgroundColor = '#ffffff';
                }
                if (level.options.length === 0) {
                    columnStyle.backgroundColor = 'transparent';
                }
                return (React.createElement("div", { className: `${clsPrefix}-column`, style: columnStyle, key: index },
                    React.createElement(CascaderItemList, { options: level.options, loadData: loadData, clsPrefix: clsPrefix, val: val, loadingNode: loadingNode, onClick: (item) => {
                            handleListClick(item, index);
                        }, mode: mode })));
            }))) : (React.createElement(CnTab, { className: `${clsPrefix}-nav`, onChange: (key) => {
                    setTabActiveIndex(key);
                }, activeKey: tabActiveIndex, contentClassName: `${clsPrefix}-content`, tabAlign: "left" }, levels.map((level, index) => {
                const { selected } = level;
                const val = level.selected ? level.selected.value : null;
                return (React.createElement(CnTabItem, { key: index, title: selected ? selected.label : rootTipText },
                    React.createElement(CascaderItemList, { options: level.options, loadData: loadData, clsPrefix: clsPrefix, val: val, loadingNode: loadingNode, onClick: (item) => {
                            handleListClick(item, index);
                        }, mode: mode })));
            })));
    }
    return (React.createElement(SelectDrawer, { ...others, ref: ref, hasClear: hasClear, size: size, className: classNames(clsPrefix, className), content: renderContent(getDataByValues(dataSource, curVal), separator), drawerClassName: classNames(`${clsPrefix}-container`, drawerClassName), onVisibleChange: handleVisibleChange, onOk: handleOk, onCancel: handleCancel, onClear: handleClear, buttonPosition: "bottom" },
        search,
        content));
};
const CnCascaderSelect = forwardRef(CascaderSelect);
CnCascaderSelect.displayName = 'CnCascaderSelect';
CnCascaderSelect.defaultProps = {
    separator: '/',
    mode: 'cascade',
    requestConfig: {},
    size: 'medium',
    useSimpleValue: true,
    filterLocal: true,
};
export { CnCascaderSelect };
